import { useState, useEffect, useRef } from 'react';

interface EyePosition {
  x: number;
  y: number;
}

interface UseEyeTrackingProps {
  maxDistance: number;
  forceLookX: number | undefined;
  forceLookY: number | undefined;
}

export function useEyeTracking({ maxDistance, forceLookX, forceLookY }: UseEyeTrackingProps) {
  const [pos, setPos] = useState<EyePosition>({ x: 0, y: 0 });
  const eyeRef = useRef<HTMLDivElement>(null);
  const targetMouseRef = useRef({ x: 0, y: 0 });
  const smoothPosRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      targetMouseRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  useEffect(() => {
    if (forceLookX !== undefined && forceLookY !== undefined) {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
      // Синхронизируем smoothPosRef, чтобы lerp стартовал отсюда когда forced-режим заканчивается
      smoothPosRef.current = { x: forceLookX, y: forceLookY };
      const t = setTimeout(() => setPos({ x: forceLookX, y: forceLookY }), 0);
      return () => clearTimeout(t);
    }

    const animate = () => {
      if (eyeRef.current) {
        const rect = eyeRef.current.getBoundingClientRect();
        const dx = targetMouseRef.current.x - (rect.left + rect.width / 2);
        const dy = targetMouseRef.current.y - (rect.top + rect.height / 2);
        const dist = Math.min(Math.sqrt(dx ** 2 + dy ** 2), maxDistance);
        const angle = Math.atan2(dy, dx);
        const targetX = Math.cos(angle) * dist;
        const targetY = Math.sin(angle) * dist;

        const smoothPos = smoothPosRef.current;
        smoothPos.x += (targetX - smoothPos.x) * 0.12;
        smoothPos.y += (targetY - smoothPos.y) * 0.12;
        setPos({ x: smoothPos.x, y: smoothPos.y });

        if (Math.abs(targetX - smoothPos.x) > 0.05 || Math.abs(targetY - smoothPos.y) > 0.05) {
          rafRef.current = requestAnimationFrame(animate);
        } else {
          rafRef.current = 0;
        }
      } else {
        rafRef.current = requestAnimationFrame(animate);
      }
    };
    const onMove = () => {
      if (!rafRef.current) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };
    window.addEventListener('mousemove', onMove);

    // Запускаем сразу, чтобы зрачки анимировались из forced-позиции к курсору
    // не дожидаясь движения мыши
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('mousemove', onMove);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
    };
  }, [maxDistance, forceLookX, forceLookY]);

  return { eyeRef, pos };
}
